import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Flex,
    Icon,
    IconButton,
    Image,
    Link,
    Stack,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { PropsWithChildren, useState } from 'react';
import type { IconType } from 'react-icons';
import { BsChevronBarLeft, BsChevronBarRight } from 'react-icons/bs';
import {
    Ri24HoursLine,
    RiBook2Line,
    RiCollageLine,
    RiCommandLine,
    RiCpuLine,
    RiDashboardLine,
    RiExchangeDollarLine,
    RiFileChartLine,
    RiHandCoinLine,
    RiLineChartLine,
    RiLogoutBoxLine,
    RiMoneyDollarCircleFill,
    RiOrganizationChart,
    RiPulseLine,
    RiRefreshFill,
    RiRotateLockLine,
    RiScales2Line,
    RiSearchEyeFill,
    RiShoppingCart2Line,
    RiStackLine,
    RiStopCircleFill,
    RiToolsLine,
    RiUser2Line,
} from 'react-icons/ri';
import { SiSwagger } from 'react-icons/si';
import { TbTransform } from 'react-icons/tb';
import { Link as RouterLink, useMatch } from 'react-router-dom';

import { logout } from 'views/auth/auth-queries';

import { useTradeBlotterContext } from './TradeBlotter/TradeBlotterContext';

const LS_KEY_NAV_COLLAPSE_STATE = 'LS_KEY_NAV_COLLAPSE_STATE';
export const Nav = () => {
    const [collapsed, setCollapsed] = useState(localStorage.getItem(LS_KEY_NAV_COLLAPSE_STATE) === 'collapsed');
    const { trades } = useTradeBlotterContext();

    return (
        <Flex direction="column" w={collapsed ? '56px' : '190px'} transition="all 0.2s linear" align="center" py={3}>
            <Image height={collapsed ? 5 : 6} width="auto" objectFit="contain" src="/logo-short.svg" mb={6} mt={3} />
            <Stack w="100%">
                <NavItem to="dashboard" label="Dashboard" icon={RiDashboardLine} collapsed={collapsed} isTopLevel />
                <Accordion width="100%" allowMultiple>
                    <Stack>
                        <Group
                            label="Reporting"
                            icon={RiFileChartLine}
                            collapsed={collapsed}
                            items={[
                                {
                                    to: 'security-master',
                                    label: 'Security Master',
                                    icon: RiStackLine,
                                },
                                {
                                    to: 'pricing',
                                    label: 'Pricing',
                                    icon: RiScales2Line,
                                },
                                {
                                    to: 'portfolios',
                                    label: 'Portfolios',
                                    icon: RiBook2Line,
                                },
                                {
                                    to: 'positions',
                                    label: 'Positions',
                                    icon: RiLineChartLine,
                                },
                                {
                                    to: 'lots',
                                    label: 'Lots',
                                    icon: RiOrganizationChart,
                                },
                                {
                                    to: 'trade-history',
                                    label: 'Trade History',
                                    icon: RiExchangeDollarLine,
                                },
                                {
                                    to: 'transactions',
                                    label: 'Transactions',
                                    icon: TbTransform,
                                },
                                {
                                    to: 'restrictions',
                                    label: 'Restrictions',
                                    icon: RiRotateLockLine,
                                },
                                {
                                    to: 'withdrawals',
                                    label: 'Withdrawals',
                                    icon: RiHandCoinLine,
                                },
                                {
                                    to: 'allocation',
                                    label: 'Allocation',
                                    icon: RiCollageLine,
                                },
                            ]}
                        />
                        <NavItem
                            to="portfolio-watcher"
                            label="Account Watcher"
                            icon={RiSearchEyeFill}
                            collapsed={collapsed}
                            isTopLevel
                        />
                        <Group
                            label="Portfolio Tracking"
                            icon={RiPulseLine}
                            collapsed={collapsed}
                            items={[
                                {
                                    to: 'suspended-portfolios',
                                    label: 'Suspended',
                                    icon: RiStopCircleFill,
                                },
                                {
                                    to: 'client-directed',
                                    label: 'Client Directed',
                                    icon: RiUser2Line,
                                },
                                {
                                    to: 'unlinked-funded',
                                    label: 'Unlinked & Funded',
                                    icon: RiMoneyDollarCircleFill,
                                },
                                {
                                    to: 'off-balance',
                                    label: 'Off Balance',
                                    icon: RiRefreshFill,
                                },
                            ]}
                        />

                        <Group
                            label="Trading Tools"
                            icon={RiToolsLine}
                            collapsed={collapsed}
                            items={[
                                {
                                    to: 'optimizer',
                                    label: 'Optimizer',
                                    icon: RiCpuLine,
                                },
                                {
                                    to: 'trade-blotter',
                                    label: 'Trade Blotter',
                                    icon: RiShoppingCart2Line,
                                    decoration:
                                        trades.length > 0 &&
                                        (collapsed ? (
                                            <Flex
                                                position="absolute"
                                                top="-2px"
                                                right="-4px"
                                                background="red.600"
                                                textAlign="center"
                                                borderRadius="full"
                                                justifyContent="center"
                                                alignItems="center"
                                                width="20px"
                                                height="20px"
                                                zIndex={100}
                                            >
                                                <Text fontSize="xs" color="white" as="span" lineHeight={1}>
                                                    {trades.length}
                                                </Text>
                                            </Flex>
                                        ) : (
                                            <Flex
                                                position="absolute"
                                                bottom={0}
                                                right={0}
                                                background="red.600"
                                                width="30px"
                                                height="100%"
                                                textAlign="center"
                                                borderTopRightRadius="md"
                                                borderBottomRightRadius="md"
                                                justifyContent="center"
                                                alignItems="center"
                                            >
                                                <Text color="white" fontSize="xs">
                                                    {trades.length}
                                                </Text>
                                            </Flex>
                                        )),
                                },
                            ]}
                        />
                    </Stack>
                </Accordion>
            </Stack>
            <Stack w="100%" mt="auto">
                <NavItem to="swagger" label="Swagger" icon={SiSwagger} collapsed={collapsed} />
                <NavItem to="/" label="Claro Intelligent Hub" icon={RiCommandLine} collapsed={collapsed} />
                <NavButton
                    label={collapsed ? 'Expand' : 'Collapse'}
                    icon={collapsed ? BsChevronBarRight : BsChevronBarLeft}
                    collapsed={collapsed}
                    variant="ghost"
                    onClick={() =>
                        setCollapsed((state) => {
                            localStorage.setItem(LS_KEY_NAV_COLLAPSE_STATE, state ? 'open' : 'collapsed');
                            return !state;
                        })
                    }
                />
                <NavButton label="Logout" icon={RiLogoutBoxLine} collapsed={collapsed} onClick={logout} />
            </Stack>
        </Flex>
    );
};

const Group = ({
    label,
    icon,
    collapsed,
    items,
}: {
    label: string;
    icon: IconType;
    collapsed: boolean;
    items: { to: string; label: string; icon: IconType; decoration?: React.ReactNode; isWip?: boolean }[];
}) => {
    return (
        <AccordionItem border={0}>
            <Tooltip label={label} placement="right" isDisabled={!collapsed}>
                <AccordionButton p={0} m={0} pl={3} height={collapsed ? '44px' : '30px'}>
                    <Icon boxSize={collapsed ? 6 : 5} mr={collapsed ? -1 : 3} as={icon} />
                    {!collapsed && <Text fontSize="sm">{label}</Text>}
                    <AccordionIcon boxSize={collapsed ? 6 : 5} />
                </AccordionButton>
            </Tooltip>

            <AccordionPanel p={0} pt={1} pb={1} mt={1} background="whiteAlpha.200" borderRightRadius="md">
                <Stack gap={1}>
                    {items.map((item) => (
                        <NavItem
                            key={item.to}
                            to={item.to}
                            label={item.label}
                            icon={item.icon}
                            collapsed={collapsed}
                            isWip={item.isWip}
                        >
                            {item.decoration}
                        </NavItem>
                    ))}
                </Stack>
            </AccordionPanel>
        </AccordionItem>
    );
};

const NavButton = ({
    label,
    icon,
    collapsed,
    onClick,
    isLoading,
    variant,
}: {
    label: string;
    icon: IconType;
    collapsed: boolean;
    isLoading?: boolean;
    onClick: () => void;
    variant?: string;
}) => {
    return (
        <Box w="100%" px={2}>
            {collapsed ? (
                <Tooltip label={label} placement="right">
                    <IconButton
                        aria-label="logout"
                        width="100%"
                        icon={<Icon as={icon} />}
                        onClick={onClick}
                        isLoading={isLoading}
                        variant={variant}
                    />
                </Tooltip>
            ) : (
                <Button width="100%" leftIcon={<Icon as={icon} />} onClick={onClick} variant={variant} size="sm">
                    <Text fontSize="sm">{label}</Text>
                </Button>
            )}
        </Box>
    );
};

const NavItem = ({
    to,
    label,
    icon,
    collapsed,
    isWip,
    isTopLevel,
    children,
}: PropsWithChildren<{
    to: string;
    label: string;
    icon: IconType;
    collapsed: boolean;
    isWip?: boolean;
    isTopLevel?: boolean;
}>) => {
    const match = useMatch('/:route/*')?.params.route;
    return (
        <Tooltip label={label} isDisabled={!collapsed} placement="right">
            <Box w="100%" px={2}>
                <Link
                    h={collapsed ? '38px' : '30px'}
                    px={1}
                    transition="all 0.2s linear"
                    to={to}
                    as={RouterLink}
                    backgroundColor={match === to ? 'teal.200' : ''}
                    color={match === to ? 'gray.800' : ''}
                    fontWeight={match === to ? 'medium' : ''}
                    display="flex"
                    w="100%"
                    borderRadius="md"
                    position="relative"
                    _hover={{
                        backgroundColor: match === to ? 'teal.200' : 'teal.700',
                    }}
                >
                    <Flex align="center" justify={collapsed ? 'center' : 'start'} gap={2} w="100%">
                        <Icon boxSize={collapsed ? 7 : 6} as={icon} />
                        {!collapsed && <Text fontSize={isTopLevel ? 'sm' : 'xs'}>{label}</Text>}
                    </Flex>
                    {isWip && (
                        <Flex
                            position="absolute"
                            bottom={0}
                            left={collapsed ? 0 : 'unset'}
                            right={collapsed ? 'unset' : 0}
                            background="red.600"
                            width={collapsed ? '100%' : '30px'}
                            height={collapsed ? 'auto' : '100%'}
                            textAlign="center"
                            borderBottomLeftRadius={collapsed ? 'md' : 'initial'}
                            borderTopRightRadius={collapsed ? 'initial' : 'md'}
                            borderBottomRightRadius="md"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Text color="white" fontSize="9px">
                                WIP
                            </Text>
                        </Flex>
                    )}
                    {children}
                </Link>
            </Box>
        </Tooltip>
    );
};
